import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Chip, Stack, LinearProgress,
  Button, IconButton, alpha, useTheme, CircularProgress,
  Drawer, Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions,
  DialogContentText,
} from '@mui/material';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import { useQuiz, useApp } from '../contexts';
import { isAnswerCorrect } from '../utils';
import QuizQuestionCard from '../components/QuizQuestionCard';

export default function QuizActive() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { state } = useApp();
  const theme = useTheme();
  const {
    session, currentQuestion, isCorrectAtCurrent, progress,
    correctCount, answeredCount, totalCount, formattedTime,
    startQuiz, selectAnswer, goNext, goPrev, jumpTo, clearSession, finishQuiz,
  } = useQuiz();

  const [showAnswerSheet, setShowAnswerSheet] = useState(false);
  const [answerSheetTab, setAnswerSheetTab] = useState(0);
  const [isStarting, setIsStarting] = useState(false);
  const [revealedIds, setRevealedIds] = useState<Set<string>>(new Set());
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const startedRef = useRef(false);

  const homeworkMode = session?.mode === 'homework' ? true : session?.mode ? false : state.settings.homeworkMode;
  const halfExamMode = session?.mode === 'halfExam' ? true : session?.mode ? false : state.settings.halfExamMode;
  const examMode = session?.mode === 'exam' ? true : session?.mode ? false : state.settings.examMode;

  // Start quiz from URL params
  useEffect(() => {
    if (startedRef.current) return;
    const bankId = searchParams.get('bank');
    if (!bankId || state.banks.length === 0) return;
    const bank = state.banks.find(b => b.id === bankId);
    if (!bank) return;
    const countStr = searchParams.get('count');
    const count = countStr ? parseInt(countStr, 10) : state.settings.defaultQuickCount;
    const finalCount = count > 0 ? Math.min(count, bank.hqb.questions.length) : bank.hqb.questions.length;
    startedRef.current = true;
    setIsStarting(true);
    setTimeout(() => { startQuiz(bank, finalCount); setIsStarting(false); }, 150);
  }, [searchParams, state.banks, state.settings.defaultQuickCount, startQuiz]);

  if (isStarting) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 2, flexDirection: 'column' }}>
        <CircularProgress size={40} />
        <Typography color="text.secondary">正在准备题目...</Typography>
      </Box>
    );
  }

  if (!session || !currentQuestion) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 2, flexDirection: 'column', p: 4 }}>
        <Typography color="text.secondary">没有正在进行的刷题会话</Typography>
        <Button variant="outlined" onClick={() => navigate('/home')} sx={{ borderRadius: 3 }}>
          返回首页选择题库
        </Button>
      </Box>
    );
  }

  const handleOptionClick = (option: string) => {
    if (session.isFinished) return;
    selectAnswer(currentQuestion.id, option);
  };

  const handleExit = () => {
    if (session.isFinished) { clearSession(); navigate('/home'); return; }
    setShowExitDialog(true);
  };

  const handleSubmitAndGrade = () => {
    finishQuiz();
    setShowExitDialog(false);
    navigate('/result');
  };

  const handleResumeLater = () => {
    setShowExitDialog(false);
    navigate('/home');
  };

  const handleDiscard = () => {
    setShowExitDialog(false);
    setShowDiscardConfirm(true);
  };

  const handleConfirmDiscard = () => {
    clearSession();
    setShowDiscardConfirm(false);
    navigate('/home');
  };

  const handleRevealAnswer = () => {
    const qId = currentQuestion.id;
    setRevealedIds(prev => new Set(prev).add(qId));
  };

  const handleNextOrFinish = () => {
    if (session.currentIndex >= session.questions.length - 1) finishQuiz();
    else {
      // Clear reveal state for next question
      setRevealedIds(prev => {
        const next = new Set(prev);
        next.delete(currentQuestion.id);
        return next;
      });
      goNext();
    }
  };

  const accuracy = totalCount > 0 ? Math.round((correctCount / totalCount) * 100) : 0;
  const currentAnswer = session.answers[currentQuestion.id] || [];
  const isRevealed = revealedIds.has(currentQuestion.id);
  const isWrong = isCorrectAtCurrent === false;
  const canProceed = true;

  // Build answer sheet data
  const questionsByGroup = (() => {
    const groups: { name: string; questions: typeof session.questions }[] = [];
    const bank = state.banks.find(b => b.id === session.bankId);
    if (bank && bank.hqb.groups.length > 0) {
      bank.hqb.groups
        .sort((a, b) => a.order - b.order)
        .forEach(g => {
          const qs = session.questions.filter(q => g.questionIds.includes(q.id));
          if (qs.length > 0) groups.push({ name: g.name, questions: qs });
        });
      const groupedIds = new Set(bank.hqb.groups.flatMap(g => g.questionIds));
      const ungrouped = session.questions.filter(q => !groupedIds.has(q.id));
      if (ungrouped.length > 0) groups.push({ name: '未分类', questions: ungrouped });
    } else {
      groups.push({ name: '全部', questions: session.questions });
    }
    return groups;
  })();

  const isFinished = session.isFinished;

  return (
    <Box sx={{ height: '100dvh', overflow: 'auto' }}>
      {/* Sticky top bar */}
      <Box
        sx={{
          position: 'sticky', top: 0, zIndex: 11,
          bgcolor: alpha(theme.palette.background.default, 0.92),
          backdropFilter: 'blur(16px)',
          borderBottom: '1px solid', borderColor: 'divider',
        }}
      >
        {/* Row 1 — navigation */}
        <Stack direction="row" sx={{ px: 1.5, py: 1, alignItems: 'center' }}>
          <IconButton onClick={handleExit} size="small" sx={{ mr: 0.5 }}>
            <ArrowBackIosNewRoundedIcon fontSize="small" />
          </IconButton>
          <Typography variant="subtitle2" noWrap sx={{ flex: 1, fontWeight: 600 }}>
            {state.banks.find(b => b.id === session.bankId)?.name || '刷题中'}
          </Typography>
          <Chip label={`${session.currentIndex + 1}/${totalCount}`} size="small" variant="outlined" sx={{ fontWeight: 600, mr: 0.5 }} />
          {examMode && !isFinished && (
            <IconButton size="small" onClick={handleSubmitAndGrade} sx={{ mr: 0.5 }} color="error">
              <CheckCircleOutlineRoundedIcon fontSize="small" />
            </IconButton>
          )}
          <IconButton size="small" onClick={() => setShowAnswerSheet(true)}>
            <GridViewRoundedIcon fontSize="small" />
          </IconButton>
        </Stack>

        {/* Progress bar */}
        <LinearProgress
          variant="determinate"
          value={progress * 100}
          sx={{
            height: 3, bgcolor: alpha(theme.palette.primary.main, 0.12),
            '& .MuiLinearProgress-bar': { transition: 'transform 0.3s ease' },
          }}
        />

        {/* Row 2 — status */}
        <Stack direction="row" sx={{ px: 1.5, py: 1, alignItems: 'center', justifyContent: 'space-between' }}>
          <Stack direction="row" spacing={0.75}>
            {formattedTime && (
              <Chip icon={<AccessTimeRoundedIcon sx={{ fontSize: 14 }} />} label={formattedTime} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontWeight: 500 }} />
            )}
            {!examMode && (
            <Chip
              icon={<CheckCircleOutlineRoundedIcon sx={{ fontSize: 14 }} />}
              label={`${correctCount}/${totalCount}`}
              size="small"
              color={accuracy >= 80 ? 'success' : accuracy >= 60 ? 'warning' : 'error'}
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
            )}
            {homeworkMode && <Chip label="作业" size="small" color="info" variant="outlined" sx={{ fontWeight: 600 }} />}
            {halfExamMode && <Chip label="半考试" size="small" color="warning" variant="outlined" sx={{ fontWeight: 600 }} />}
          </Stack>
          <Stack direction="row" spacing={0.75}>
            {currentAnswer.length > 0 && !isFinished && !examMode && !(halfExamMode && isWrong && !isRevealed) && (
              <Chip
                size="small"
                icon={isCorrectAtCurrent === true ? <CheckCircleOutlineRoundedIcon sx={{ fontSize: 14 }} /> : <CancelOutlinedIcon sx={{ fontSize: 14 }} />}
                label={isCorrectAtCurrent === true ? '正确' : '错误'}
                color={isCorrectAtCurrent === true ? 'success' : 'error'}
              />
            )}
            {halfExamMode && isWrong && !isRevealed && !isFinished && (
              <Button
                variant="outlined" color="warning" size="small"
                startIcon={<VisibilityRoundedIcon />}
                onClick={handleRevealAnswer}
                sx={{ borderRadius: 3, lineHeight: 1.2 }}
              >
                查看答案
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>

      {/* Main content */}
      <Box sx={{ pb: 10 }}>
        <Box sx={{ maxWidth: 640, mx: 'auto', px: 2, pt: 2 }}>

          {/* Question card */}
          <QuizQuestionCard
            question={currentQuestion}
            selectedOptions={currentAnswer}
            onOptionClick={handleOptionClick}
            showExplanation={isFinished && state.settings.showExplanation}
            disabled={isFinished}
            revealAnswer={!examMode && (!halfExamMode || isRevealed)}
          />

          {/* Finished actions */}
          {isFinished && (
            <Box sx={{ mt: 3 }}>
              <Button fullWidth variant="contained" size="large" onClick={() => navigate('/result')} sx={{ borderRadius: 3, py: 1.5 }}>
                查看成绩
              </Button>
              <Button fullWidth variant="outlined" size="large" onClick={handleExit} sx={{ borderRadius: 3, py: 1.5, mt: 1.5 }}>
                返回首页
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      {/* Bottom navigation bar */}
      {!isFinished && (
        <Box
          sx={{
            position: 'fixed', bottom: 0, left: 0, right: 0,
            px: 2, py: 1.5,
            bgcolor: alpha(theme.palette.background.paper, 0.92),
            backdropFilter: 'blur(20px)',
            borderTop: '1px solid', borderColor: 'divider', zIndex: 10,
          }}
        >
          <Stack direction="row" spacing={1.5} sx={{ maxWidth: 640, mx: 'auto' }}>
            <Button
              variant="outlined" size="large" fullWidth
              startIcon={<ArrowBackIosNewRoundedIcon />}
              onClick={goPrev}
              disabled={session.currentIndex === 0}
              sx={{ borderRadius: 3, py: 1.2 }}
            >
              上一题
            </Button>
            <Button
              variant="contained" size="large" fullWidth
              endIcon={<ArrowForwardIosRoundedIcon />}
              onClick={handleNextOrFinish}
              disabled={!canProceed}
              sx={{ borderRadius: 3, py: 1.2 }}
            >
              {session.currentIndex >= session.questions.length - 1 ? '交卷' : '下一题'}
            </Button>
          </Stack>
        </Box>
      )}

      {/* Answer sheet drawer */}
      <Drawer
        anchor="right"
        open={showAnswerSheet}
        onClose={() => setShowAnswerSheet(false)}
        slotProps={{ paper: { sx: { width: { xs: '85vw', sm: 400 }, borderRadius: 0 } } }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Stack direction="row" sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider', alignItems: 'center' }}>
            <Typography variant="subtitle1" sx={{ flex: 1, fontWeight: 600 }}>答题卡</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
              {examMode ? `${answeredCount}/${totalCount}` : `${correctCount}/${totalCount}`}
            </Typography>
            <IconButton size="small" onClick={() => setShowAnswerSheet(false)}>
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Tabs
            value={answerSheetTab} onChange={(_, v) => setAnswerSheetTab(v)}
            variant="scrollable" scrollButtons="auto"
            sx={{
              borderBottom: '1px solid', borderColor: 'divider',
              minHeight: 40, '& .MuiTab-root': { minHeight: 40, py: 0.5, px: 2, fontSize: '0.8rem' },
            }}
          >
            <Tab label={`全部 (${totalCount})`} />
            {questionsByGroup.map((g, i) => (
              <Tab key={i + 1} label={g.name} />
            ))}
          </Tabs>
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            {(answerSheetTab === 0
              ? session.questions
              : questionsByGroup[answerSheetTab - 1]?.questions || []
            ).map((q, _idx) => {
              const globalIdx = session.questions.findIndex(sq => sq.id === q.id);
              const isCurrent = globalIdx === session.currentIndex;
              const ans = session.answers[q.id];
              const answered = ans && ans.length > 0;
              const correct = !examMode && ans && isAnswerCorrect(q, ans);
              const wrong = !examMode && ans && !isAnswerCorrect(q, ans);
              return (
                <Button
                  key={q.id}
                  variant={isCurrent ? 'contained' : 'outlined'}
                  onClick={() => { jumpTo(globalIdx); setShowAnswerSheet(false); }}
                  sx={{
                    width: '100%', justifyContent: 'flex-start', py: 1.5, px: 2, mb: 1,
                    borderRadius: 3, textTransform: 'none',
                    borderColor: isCurrent ? 'primary.main'
                      : correct ? theme.palette.success.main
                      : wrong ? theme.palette.error.main
                      : answered ? 'primary.main'
                      : 'divider',
                    color: isCurrent ? 'primary.contrastText'
                      : correct ? 'success.main'
                      : wrong ? 'error.main'
                      : answered ? 'primary.main'
                      : 'text.primary',
                    bgcolor: isCurrent ? 'primary.main'
                      : correct ? alpha(theme.palette.success.main, 0.06)
                      : wrong ? alpha(theme.palette.error.main, 0.06)
                      : answered ? alpha(theme.palette.primary.main, 0.06)
                      : 'transparent',
                    fontWeight: isCurrent ? 600 : 400,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Chip size="small" label={globalIdx + 1}
                      sx={{ minWidth: 30, height: 28, px: 0.5, borderRadius: '50%', fontWeight: 600, fontSize: '0.75rem',
                        bgcolor: isCurrent ? 'rgba(255,255,255,0.25)' : 'action.hover' }} />
                    <Typography variant="body2" noWrap sx={{ flex: 1, textAlign: 'left' }}>
                      {q.content.length > 30 ? q.content.slice(0, 30) + '...' : q.content}
                    </Typography>
                    {correct && <CheckCircleOutlineRoundedIcon fontSize="small" color="success" />}
                    {wrong && <CancelOutlinedIcon fontSize="small" color="error" />}
                    {examMode && answered && !correct && !wrong && <CheckCircleOutlineRoundedIcon fontSize="small" color="primary" />}
                    {examMode && !answered && <Box sx={{ width: 16, height: 16, borderRadius: '50%', border: '1.5px solid', borderColor: 'divider' }} />}
                    {!examMode && !ans && <Box sx={{ width: 16, height: 16, borderRadius: '50%', border: '1.5px solid', borderColor: 'divider' }} />}
                  </Box>
                </Button>
              );
            })}
          </Box>
        </Box>
      </Drawer>

      {/* Exit dialog */}
      <Dialog open={showExitDialog} onClose={() => setShowExitDialog(false)} slotProps={{ paper: { sx: { borderRadius: 4, maxWidth: 360 } } }}>
        <DialogTitle sx={{ fontWeight: 600 }}>退出刷题</DialogTitle>
        <DialogContent>
          {examMode ? (
            <Stack spacing={1.5} sx={{ pt: 1 }}>
              <Button variant="contained" fullWidth size="large" sx={{ borderRadius: 3, py: 1.5 }} onClick={handleSubmitAndGrade}>
                交卷判分
              </Button>
              <Button variant="outlined" fullWidth size="large" sx={{ borderRadius: 3, py: 1.5 }} onClick={handleResumeLater}>
                暂离（保留作答记录）
              </Button>
              <Button variant="text" color="error" fullWidth size="large" sx={{ borderRadius: 3, py: 1.5 }} onClick={handleDiscard}>
                放弃退出
              </Button>
              <Button variant="text" fullWidth size="small" sx={{ borderRadius: 3, mt: 0.5 }} onClick={() => setShowExitDialog(false)}>
                继续刷题
              </Button>
            </Stack>
          ) : (
            <Stack spacing={1.5} sx={{ pt: 1 }}>
              <Button variant="contained" fullWidth size="large" sx={{ borderRadius: 3, py: 1.5 }} onClick={handleResumeLater}>
                暂离（保留作答记录）
              </Button>
              <Button variant="outlined" color="error" fullWidth size="large" sx={{ borderRadius: 3, py: 1.5 }} onClick={handleDiscard}>
                放弃退出
              </Button>
              <Button variant="text" fullWidth size="small" sx={{ borderRadius: 3, mt: 0.5 }} onClick={() => setShowExitDialog(false)}>
                继续刷题
              </Button>
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      {/* Discard confirm dialog */}
      <Dialog open={showDiscardConfirm} onClose={() => setShowDiscardConfirm(false)} slotProps={{ paper: { sx: { borderRadius: 4, maxWidth: 360 } } }}>
        <DialogTitle sx={{ fontWeight: 600, color: 'error.main' }}>确认放弃</DialogTitle>
        <DialogContent>
          <DialogContentText>
            放弃后当前所有作答记录将被永久删除，且无法恢复。确定要继续吗？
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setShowDiscardConfirm(false)} sx={{ borderRadius: 3 }}>取消</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDiscard} sx={{ borderRadius: 3 }}>确认放弃</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
